import React from "react";
import { ChevronDown, ChevronUp, RotateCcw, StickyNote } from "lucide-react";

const EMPTY_HEIGHT = 88;

/**
 * The board that sticky notes are sent to. The notes themselves are the
 * regular notes, pinned to the dashed grid area below (data-notes-board), so
 * they stay draggable/resizable. The grid grows to fit its notes live — resize
 * or drag a note lower and the panel stretches with it. "Restore all" sends
 * every note that has a previous position back there.
 */
const StickyNotesBoard = ({
  count,
  rows,
  cardH = 170,
  gap = 16,
  canRestore = false,
  onRestore,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  // The grid is sized from the notes' own cells (rows), never by measuring
  // the DOM: measuring would grow the board, which moves the notes, which
  // re-measures — the loop that used to make notes jitter while dragging.
  const minHeight =
    count > 0 ? Math.max(1, rows) * (cardH + gap) + gap : EMPTY_HEIGHT;

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-5 border-2 border-yellow-500/30 shadow-lg card-shadow">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-lg sm:text-xl font-bold text-yellow-400 flex items-center gap-2 min-w-0">
          <StickyNote className="w-5 h-5 flex-shrink-0" />
          <span className="truncate">Sticky Notes</span>
          <span className="text-xs text-gray-500 font-normal">({count})</span>
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
                aria-label="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-cyan-300" />
              </button>
              <button
                type="button"
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
                aria-label="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-cyan-300" />
              </button>
            </div>
          )}
          {canRestore && (
            <button
              type="button"
              onClick={onRestore}
              className="tap px-3 py-2 rounded-2xl bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border-2 border-purple-500/30 hover:border-purple-500 text-sm font-semibold flex items-center gap-1.5"
              title="Send every board note back to where it was before the board"
            >
              <RotateCcw className="w-4 h-4" />
              Restore all
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        Use a note's Send to board button (or drag it here) to park it on this
        board. Drag or resize notes freely — the board grows with them. Go back
        returns a note to its previous place and size.
      </p>
      <div
        data-notes-board
        className="rounded-2xl border-2 border-dashed border-gray-600/70 bg-gray-700/20 flex items-center justify-center"
        style={{ minHeight }}
      >
        {count === 0 && (
          <span className="text-sm text-gray-500 select-none">
            Send notes here
          </span>
        )}
      </div>
    </div>
  );
};

export default StickyNotesBoard;
